// src/pages/Keramika.tsx
import { motion } from 'framer-motion';
import { Tab } from '@headlessui/react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import Gallery from '@/components/Gallery';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Keramika = () => {
  const categories = [
    { id: 'videz-lesa', name: 'Videz lesa' },
    { id: 'videz-marmorja', name: 'Videz marmorja' },
    { id: 'videz-betona-kamna', name: 'Videz betona, kamna' },
    { id: 'dekor', name: 'Dekor' },
    { id: 'velikoformatna-keramika', name: 'Velikoformatna keramika' },
  ];

  const categoryData = {
    'videz-lesa': {
      title: 'Keramične ploščice videz lesa',
      description: 'Topel videz lesa z vzdržljivostjo keramike.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1581922814484-0b48460b7010?q=80&w=2070', 
          alt: 'Keramične ploščice videz lesa' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1609799408354-199f85d5ca58?q=80&w=2070', 
          alt: 'Keramične ploščice videz lesa v kopalnici' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1614632538395-6224c54be48b?q=80&w=2070', 
          alt: 'Keramične ploščice videz lesa v dnevni sobi' 
        },
      ]
    },
    'videz-marmorja': {
      title: 'Keramične ploščice videz marmorja',
      description: 'Eleganca marmorja za vaš dom.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1615529161169-7b3aece99eec?q=80&w=2070', 
          alt: 'Keramične ploščice videz marmorja' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1603825492237-67df97db9b64?q=80&w=2070', 
          alt: 'Keramične ploščice videz marmorja v kopalnici' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1610731260724-ecabdf0a8b5e?q=80&w=2070', 
          alt: 'Keramične ploščice videz marmorja v kuhinji' 
        },
      ]
    },
    'videz-betona-kamna': {
      title: 'Keramične ploščice videz betona, kamna',
      description: 'Moderen in robusten dizajn.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1574731635548-91b2d9be3db0?q=80&w=2070', 
          alt: 'Keramične ploščice videz betona' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1599297915243-a0f85704bc28?q=80&w=2070', 
          alt: 'Keramične ploščice videz kamna' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1582468546235-9bf31e5bc8a1?q=80&w=2070', 
          alt: 'Keramične ploščice videz betona v dnevni sobi' 
        },
      ]
    },
    'dekor': {
      title: 'Keramične ploščice Dekor',
      description: 'Unikatni vzorci za posebne prostore.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1585657233788-e8a05d309089?q=80&w=1974', 
          alt: 'Keramične ploščice dekor' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2158', 
          alt: 'Keramične ploščice dekor vzorci' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1599619496619-a1500e105747?q=80&w=1974', 
          alt: 'Keramične ploščice dekor v kopalnici' 
        },
      ]
    },
    'velikoformatna-keramika': {
      title: 'Velikoformatna keramika',
      description: 'Velike ploščice za brezšivne površine.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1600607687939-ce8a6c8a16c9?q=80&w=2070', 
          alt: 'Velikoformatna keramika' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1600585154084-4e5fe7c39198?q=80&w=2070', 
          alt: 'Velikoformatna keramika v kopalnici' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1604014237256-11d475e2a2d8?q=80&w=2070', 
          alt: 'Velikoformatna keramika v dnevni sobi' 
        },
      ]
    },
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main className="pt-16">
        <HeroSection 
          title="Odkrijte našo ponudbo keramičnih ploščic"
          subtitle="Vrhunska kakovost in dizajn za vsak prostor"
          imageSrc="https://images.unsplash.com/photo-1600566752355-35792bedcfea?q=80&w=2070"
        />

        <section className="py-16">
          <div className="container-custom">
            <SectionHeading 
              title="Keramične ploščice"
              subtitle="Odkrijte našo široko ponudbo keramičnih ploščic različnih stilov in dimenzij"
            />
            
            <Tab.Group>
              <Tab.List className="flex space-x-1 rounded-xl bg-stone-100 p-1 mb-8">
                {categories.map((category) => (
                  <Tab
                    key={category.id}
                    className={({ selected }) =>
                      `w-full rounded-lg py-3 text-sm font-medium leading-5 
                      ${
                        selected
                          ? 'bg-white shadow text-stone-800'
                          : 'text-stone-600 hover:bg-white/[0.12] hover:text-stone-800'
                      }`
                    }
                  >
                    {category.name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-2">
                {Object.entries(categoryData).map(([id, data]) => (
                  <Tab.Panel
                    key={id}
                    className="rounded-xl p-3"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="mb-10">
                        <h3 className="text-2xl font-bold text-stone-800 mb-3">{data.title}</h3>
                        <p className="text-lg text-stone-600 mb-6">{data.description}</p>
                        <Gallery images={data.images} />
                        <div className="mt-8 flex justify-center">
                          <Button className="bg-secondary hover:bg-secondary/90 text-white font-semibold flex items-center gap-2" asChild>
                            <a href="/kontakt">
                              Zahtevaj ponudbo <ArrowRight size={16} />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Keramika;